import { useField } from 'formik';
import { memo, Suspense, useEffect } from 'react';
import { graphql, useQueryLoader } from 'react-relay';

import usePaginatorSearchOverlayQueryLoader from '../../../hooks/usePaginatorSearchOverlayQueryLoader';
import {
  UnitPaginatorSearchOverlayTokenField_unitsQuery,
  UnitPaginatorSearchOverlayTokenField_unitsQuery$variables,
} from '../../../relay/__generated__/UnitPaginatorSearchOverlayTokenField_unitsQuery.graphql';
import { isNullable } from '../../../utils/is';
import Checkbox from '../../core/Checkbox';
import EmptyState from '../../core/EmptyState';
import Grid from '../../core/Grid';
import ItemList from '../../core/ItemList';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import ScrollContainer from '../../core/ScrollContainer';
import SearchOverlayTokenField, { SearchOverlayTokenFieldProps } from '../../core/SearchOverlayTokenField';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import View from '../../core/View';
import UnitPaginator from '../UnitPaginator';

const unitsForUnitPaginatorSearchOverlayTokenField = graphql`
  query UnitPaginatorSearchOverlayTokenField_unitsQuery(
    $filters: UnitFilter
    $order: UnitOrder
    $first: Int
    $after: String
  ) {
    units(filters: $filters, order: $order, first: $first, after: $after) {
      edges {
        node {
          id
          title
        }
      }
    }
    ...UnitPaginator_query @arguments(filters: $filters, order: $order, first: $first, after: $after)
  }
`;

type Props = {
  variables?: UnitPaginatorSearchOverlayTokenField_unitsQuery$variables;
} & Omit<SearchOverlayTokenFieldProps, 'renderItemList'>;

const UnitPaginatorSearchOverlayTokenField = ({ name = '', value: propValue, variables, ...props }: Props) => {
  const [{ value: baseValue }] = useField<string[] | undefined>({ name });
  const value = !isNullable(propValue) ? propValue : baseValue;

  const [valueQueryReference, loadValueQuery, disposeValueQuery] =
    useQueryLoader<UnitPaginatorSearchOverlayTokenField_unitsQuery>(unitsForUnitPaginatorSearchOverlayTokenField);
  useEffect(() => {
    loadValueQuery({ filters: { id_In: value || [] } });
    return () => disposeValueQuery();
  }, [baseValue, loadValueQuery, disposeValueQuery]);

  const [searchOverlayFieldProps, queryReference] =
    usePaginatorSearchOverlayQueryLoader<UnitPaginatorSearchOverlayTokenField_unitsQuery>(
      unitsForUnitPaginatorSearchOverlayTokenField,
      variables,
    );

  return valueQueryReference ? (
    <PreloadedQueryRenderer query={unitsForUnitPaginatorSearchOverlayTokenField} queryReference={valueQueryReference}>
      {({ units }) => (
        <SearchOverlayTokenField
          name={name}
          value={value}
          renderTokenText={(id) => units?.edges.find(({ node }) => node.id === id)?.node.title || ''}
          renderItemList={({ containerRef, handleSelect }, itemProps) =>
            queryReference ? (
              <Suspense fallback={<Spinner sx={{ paddingY: 3 }} />}>
                <PreloadedQueryRenderer
                  query={unitsForUnitPaginatorSearchOverlayTokenField}
                  queryReference={queryReference}
                >
                  {(queryData) => (
                    <UnitPaginator fragmentReference={queryData}>
                      {(data, { hasNext, isLoadingNext, loadMore }) => (
                        <ScrollContainer
                          ref={containerRef as React.RefObject<HTMLDivElement>}
                          onScrollReachEnd={() => {
                            if (hasNext && !isLoadingNext) loadMore(20);
                          }}
                        >
                          <ItemList
                            items={data.units.edges}
                            renderItem={({ node: { title } }) => <Text sx={{ fontSize: 1 }}>{title}</Text>}
                            renderItemWrapper={(children, { node: { id } }) => (
                              <View
                                {...itemProps}
                                key={id}
                                onClick={(e) => handleSelect(e, id)}
                                onKeyDown={(e) => handleSelect(e, id)}
                              >
                                <Grid gapX={2} wrap={false}>
                                  <Grid.Unit size={'min'}>
                                    <Checkbox checked={value?.some((selectedId) => selectedId === id)} />
                                  </Grid.Unit>
                                  <Grid.Unit size={'max'}>{children}</Grid.Unit>
                                </Grid>
                              </View>
                            )}
                            emptyState={
                              <View sx={{ paddingY: 3 }}>
                                <EmptyState
                                  title={'검색 결과가 없어요'}
                                  description={'다른 검색어로 다시 시도해주세요.'}
                                />
                              </View>
                            }
                          />
                          <View>{isLoadingNext ? <Spinner size={'small'} /> : null}</View>
                        </ScrollContainer>
                      )}
                    </UnitPaginator>
                  )}
                </PreloadedQueryRenderer>
              </Suspense>
            ) : null
          }
          {...searchOverlayFieldProps}
          {...props}
        />
      )}
    </PreloadedQueryRenderer>
  ) : (
    <Spinner size={'small'} />
  );
};

export default memo(UnitPaginatorSearchOverlayTokenField);
